/**
 * Денежный формат планирования — одна точка правды на все экраны (AK-704).
 *
 * UAT founder 20.08 (прод v1.1746), дословно: «во всех справочниках все цены
 * до сотых округляем и показываем разделители разрядов пробелом».
 *
 * Два разных правила, которые легко перепутать:
 *
 *  • **Цена** (из справочника: плановая цена ТМЦ, расценка работы, цена
 *    реализации, себестоимость тонны) — ВСЕГДА две цифры после запятой, даже
 *    у круглого числа: «11 000,00 ₽». Копейки тут не украшение — по этим
 *    числам сходятся построчные суммы с итогами.
 *  • **Агрегат** (итого затрат по площади, выручка плана, ₽/га) — округляется
 *    до рубля или показывается в млн: «12 456 789 ₽», «12,5 млн ₽». Копейки в
 *    сумме на 60 000 га — шум, и такие места живут в `planVariant.ts` /
 *    `techCards.ts` со своим форматом. Этот модуль ими не занимается.
 *
 * Разделитель разрядов даёт `Intl.NumberFormat('ru-RU')` — это U+00A0
 * (неразрывный пробел). Именно пробел, как и просил founder; неразрывный —
 * чтобы «136 963,63» не переносилось по строке пополам.
 */

const MONEY_FORMAT = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** «156 109,09» — без символа валюты, для редактируемого поля цены. */
export function formatMoneyNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '';
  return MONEY_FORMAT.format(value);
}

/**
 * То же число, но ДЛЯ ЯЧЕЙКИ: пусто → «—».
 *
 * Отдельная функция, а не правка `formatMoneyNumber` (нит ревью 26.08 просил
 * именно её). `formatMoneyNumber` кормит `value` у `<Input>` в PriceCell, и
 * пустая строка там — единственно верное значение: с «—» внутри поля человек
 * начнёт стирать прочерк, а `parsePriceInput('—')` вернёт «мусор». Поэтому
 * семантики разведены: '' — для поля ввода, '—' — для чтения.
 */
export function formatMoneyCell(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return MONEY_FORMAT.format(value);
}

/**
 * «156 109,09 ₽» — для чтения. Пусто отдаём прочерком, а НЕ нулём: «цены нет»
 * и «цена ноль» — разные утверждения («пусто ≠ мусор»).
 */
export function formatPrice(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${MONEY_FORMAT.format(value)} ₽`;
}

/** «156 109,09 ₽/т» и подобное — цена за единицу, тот же формат + суффикс. */
export function formatPricePerUnit(
  value: number | null | undefined,
  unitSuffix: string,
): string {
  if (value === null || value === undefined) return '—';
  return `${MONEY_FORMAT.format(value)} ₽/${unitSuffix}`;
}

/**
 * Норма расхода топлива — «22», «4,45», «0,75». НЕ деньги: правило «всегда две
 * цифры после запятой» тут не применяется, литраж в книге нормативов записан с
 * разной точностью, и дорисовывать «22,00» значит показывать точность, которой
 * в источнике нет. Пусто — прочерк, а не ноль: «норма не указана» и «топливо не
 * расходуется» — разные утверждения.
 */
export function formatFuelRate(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(value);
}

/** «1 350,50» → 1350.5; пусто → null; мусор → undefined (не трогаем поле). */
export function parsePriceInput(raw: string): number | null | undefined {
  // \s не ловит U+00A0 в некоторых движках — режем неразрывный пробел явно,
  // иначе значение, скопированное С ЭКРАНА, читалось бы как мусор.
  const trimmed = raw.trim().replace(/[\s\u00A0]/g, '').replace(',', '.');
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return undefined;
  return value;
}

/*
 * Почему здесь НЕТ исключений по разделам.
 *
 * Был `RATE_SECTIONS` (`work_rate` / `outsource`) и формат до четырёх знаков:
 * их `planned_price` — расценка ₽ за единицу, и на проде 122 из 150 расценок
 * различались в четвёртом знаке.
 *
 * Founder снял правило 21.08: «да не может ни у каких цен быть больше 2х
 * знаков после запятой… как ты можешь заплатить человеку 0,4111 рубля — вот
 * эти 0,0011 рубля это что??». Расценка — тариф, по которому платят человеку;
 * четвёртый знак в ней хвост от деления в Excel, а не заданная точность.
 *
 * Показ и хранение теперь совпадают: два знака везде.
 */
